import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import {
  Loader2,
  X,
  Camera,
  SwitchCamera,
  RotateCcw,
  Check,
} from "lucide-react";
import { toast } from "sonner";

interface CameraModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCapture: (file: File) => void;
  /** Espelha preview e foto salva (efeito selfie). Só use quando o espelhamento da foto for desejado. */
  mirror?: boolean;
  /** Mostra um quadrado verde de enquadramento e corta a foto nele (1:1). */
  squareGuide?: boolean;
}

const GUIDE_RATIO = 0.8;

export function CameraModal({
  isOpen,
  onClose,
  onCapture,
  mirror = false,
  squareGuide = false,
}: CameraModalProps) {
  const viewRef = useRef<HTMLDivElement>(null);
  const [guideSide, setGuideSide] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [facingMode, setFacingMode] = useState<"user" | "environment">("environment");
  const [cameraState, setCameraState] = useState<"loading" | "ready" | "error" | "captured">("loading");
  const [hasMultipleCameras, setHasMultipleCameras] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  // O preview da câmera frontal fica espelhado (como num espelho, é o que o usuário espera ver),
  // mas a foto salva só é espelhada se a tela pedir (mirror). Espelhar o arquivo inverte textos e
  // referências da imagem, que deve ficar na mesma orientação em que foi capturada.
  const isPreviewMirrored = mirror || facingMode === "user";
  const isPhotoMirrored = mirror;

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        track.stop();
      });
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  const startCamera = useCallback(async (mode: "user" | "environment") => {
    setCameraState("loading");
    setErrorMessage("");
    setCapturedImage(null);
    stopStream();

    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error("Seu navegador não suporta acesso à câmera");
      }

      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(d => d.kind === "videoinput");
      setHasMultipleCameras(videoDevices.length > 1);

      const constraints: MediaStreamConstraints = {
        video: videoDevices.length > 0 ? {
          facingMode: mode,
          width: { ideal: 1280 },
          height: { ideal: 720 }
        } : true,
        audio: false
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);

      if (!videoRef.current) {
        throw new Error("Video element not available");
      }

      streamRef.current = stream;
      const video = videoRef.current;

      video.srcObject = stream;
      video.muted = true;
      video.playsInline = true;

      await video.play();

      await new Promise(resolve => setTimeout(resolve, 300));

      setCameraState("ready");
    } catch (error: any) {
      console.error("Erro ao acessar a câmera:", error);
      stopStream();

      let message = "Não foi possível acessar a câmera.";
      if (error.name === "NotAllowedError" || error.name === "PermissionDeniedError") {
        message = "Permissão de câmera negada. Verifique as configurações do navegador.";
      } else if (error.name === "NotFoundError" || error.name === "DevicesNotFoundError") {
        message = "Nenhuma câmera encontrada no dispositivo.";
      } else if (error.name === "NotReadableError" || error.name === "TrackStartError") {
        message = "Câmera em uso por outro aplicativo.";
      } else if (error.message) {
        message = error.message;
      }

      setErrorMessage(message);
      setCameraState("error");
      toast.error(message);
    }
  }, [stopStream]);

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        startCamera(facingMode);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      stopStream();
      setCameraState("loading");
      setCapturedImage(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    return () => {
      stopStream();
    };
  }, [stopStream]);

  const handleCapture = useCallback((e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    try {
      const video = videoRef.current;

      if (!video) {
        toast.error("Erro: elemento de vídeo não encontrado");
        return;
      }

      const videoWidth = video.videoWidth || video.clientWidth || 640;
      const videoHeight = video.videoHeight || video.clientHeight || 480;

      if (videoWidth === 0 || videoHeight === 0) {
        toast.error("Câmera ainda não está pronta. Aguarde.");
        return;
      }

      // Região do vídeo a capturar: tudo, ou só o quadrado do guia (o <video> usa
      // object-cover, então converte as coordenadas da tela pras do vídeo).
      let sx = 0;
      let sy = 0;
      let sw = videoWidth;
      let sh = videoHeight;

      if (squareGuide && video.clientWidth > 0 && video.clientHeight > 0) {
        const scale = Math.max(
          video.clientWidth / videoWidth,
          video.clientHeight / videoHeight
        );
        const side = Math.min(video.clientWidth, video.clientHeight) * GUIDE_RATIO;
        const offsetX = (videoWidth * scale - video.clientWidth) / 2;
        const offsetY = (videoHeight * scale - video.clientHeight) / 2;
        sw = sh = side / scale;
        sx = (video.clientWidth / 2 - side / 2 + offsetX) / scale;
        sy = (video.clientHeight / 2 - side / 2 + offsetY) / scale;
      }

      const canvas = document.createElement("canvas");
      canvas.width = Math.round(sw);
      canvas.height = Math.round(sh);

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        toast.error("Erro ao processar imagem");
        return;
      }

      if (isPhotoMirrored) {
        ctx.translate(canvas.width, 0);
        ctx.scale(-1, 1);
      }

      ctx.drawImage(video, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);

      const imageDataUrl = canvas.toDataURL("image/jpeg", 0.9);

      if (!imageDataUrl || imageDataUrl === "data:,") {
        toast.error("Erro: imagem vazia capturada");
        return;
      }

      setCapturedImage(imageDataUrl);
      setCameraState("captured");
      stopStream();
    } catch (error) {
      console.error("Error capturing photo:", error);
      toast.error("Erro ao capturar foto");
    }
  }, [isPhotoMirrored, squareGuide, stopStream]);

  const handleRetake = useCallback(() => {
    setCapturedImage(null);
    startCamera(facingMode);
  }, [facingMode, startCamera]);

  const handleClose = useCallback(() => {
    stopStream();
    setCapturedImage(null);
    setCameraState("loading");
    onClose();
  }, [stopStream, onClose]);

  const handleApply = useCallback(() => {
    if (!capturedImage) return;

    const byteString = atob(capturedImage.split(",")[1]);
    const mimeString = capturedImage.split(",")[0].split(":")[1].split(";")[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);

    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }

    const blob = new Blob([ab], { type: mimeString });
    const file = new File([blob], `foto_${Date.now()}.jpg`, { type: "image/jpeg" });

    onCapture(file);
    handleClose();
  }, [capturedImage, onCapture, handleClose]);

  const handleSwitchCamera = useCallback(() => {
    const newMode = facingMode === "user" ? "environment" : "user";
    setFacingMode(newMode);
    startCamera(newMode);
  }, [facingMode, startCamera]);

  useEffect(() => {
    if (!isOpen || !squareGuide) return;
    const element = viewRef.current;
    if (!element) return;

    const measure = () =>
      setGuideSide(Math.min(element.clientWidth, element.clientHeight) * GUIDE_RATIO);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [isOpen, squareGuide]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 bg-black flex flex-col"
      style={{ zIndex: 99999, pointerEvents: "auto" }}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-black/80">
        <Button
          variant="ghost"
          size="icon"
          onClick={handleClose}
          className="text-white hover:bg-white/20 h-12 w-12 rounded-full"
        >
          <X className="h-7 w-7" />
        </Button>
        <span className="text-white font-semibold text-lg">
          {facingMode === "user" ? "Câmera Frontal" : "Câmera"}
        </span>
        {cameraState === "ready" && hasMultipleCameras ? (
          <Button
            variant="ghost"
            size="icon"
            onClick={handleSwitchCamera}
            className="text-white hover:bg-white/20 h-12 w-12 rounded-full"
          >
            <SwitchCamera className="h-7 w-7" />
          </Button>
        ) : (
          <div className="w-12 h-12" />
        )}
      </div>

      {/* Camera View */}
      <div ref={viewRef} className="flex-1 relative bg-black overflow-hidden">
        {/* Loading State */}
        {cameraState === "loading" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 text-white">
            <Loader2 className="h-16 w-16 animate-spin" />
            <span className="text-lg">Iniciando câmera...</span>
          </div>
        )}

        {/* Error State */}
        {cameraState === "error" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 text-white text-center px-8">
            <Camera className="h-20 w-20 opacity-50" />
            <p className="text-xl font-medium">Erro na câmera</p>
            <p className="text-base opacity-70">{errorMessage}</p>
            <Button
              onClick={() => startCamera(facingMode)}
              variant="secondary"
              className="mt-4 h-12 px-6"
            >
              Tentar novamente
            </Button>
          </div>
        )}

        {/* Video Element */}
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          // Sem guia: mostra o quadro inteiro (object-contain), então o que aparece na tela é
          // exatamente o que vai na foto. Com guia: preenche a tela e recorta no quadrado.
          className={`absolute inset-0 w-full h-full bg-black ${squareGuide ? "object-cover" : "object-contain"}`}
          style={{
            transform: isPreviewMirrored ? "scaleX(-1)" : "none",
            display: cameraState === "captured" ? "none" : "block"
          }}
        />

        {/* Guia quadrado de enquadramento */}
        {squareGuide && cameraState === "ready" && guideSide > 0 && (
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <div
              className="rounded-lg border-4 border-green-500"
              style={{
                width: guideSide,
                height: guideSide,
                boxShadow: "0 0 0 9999px rgba(0, 0, 0, 0.55)",
              }}
            />
            <span className="absolute bottom-4 text-sm text-white/90">
              Encaixe o rosto no quadrado
            </span>
          </div>
        )}

        {/* Captured Image */}
        {cameraState === "captured" && capturedImage && (
          <img
            src={capturedImage}
            alt="Foto capturada"
            className="absolute inset-0 w-full h-full object-contain bg-black"
          />
        )}
      </div>

      {/* Footer Controls */}
      <div className="p-6 pb-10 bg-black/80">
        {/* Capture Button */}
        {cameraState === "ready" && (
          <div className="flex justify-center">
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleCapture(e);
              }}
              className="w-20 h-20 rounded-full bg-white border-4 border-gray-400 flex items-center justify-center shadow-2xl active:scale-90 transition-transform hover:border-gray-300 cursor-pointer"
              type="button"
            >
              <div className="w-16 h-16 rounded-full bg-white pointer-events-none" />
            </button>
          </div>
        )}

        {/* After Capture Buttons */}
        {cameraState === "captured" && (
          <div className="flex justify-center gap-6">
            <Button
              onClick={handleRetake}
              variant="outline"
              size="lg"
              className="flex-1 max-w-[160px] h-14 bg-transparent border-2 border-white text-white hover:bg-white/20 hover:text-white rounded-full"
              type="button"
            >
              <RotateCcw className="h-5 w-5 mr-2" />
              Refazer
            </Button>
            <Button
              onClick={handleApply}
              size="lg"
              className="flex-1 max-w-[160px] h-14 bg-green-500 hover:bg-green-600 text-white rounded-full"
              type="button"
            >
              <Check className="h-5 w-5 mr-2" />
              Usar foto
            </Button>
          </div>
        )}

        {/* Loading/Error state - show nothing */}
        {(cameraState === "loading" || cameraState === "error") && (
          <div className="h-20" />
        )}
      </div>
    </div>,
    document.body
  );
}
